import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShiftCard } from "@/components/ShiftCard";
import { Toast } from "@/components/Toast";
import { Logo } from "@/components/Logo";
import { WeekSelect } from "@/components/WeekSelect";
import { EmployeePayrollSummary } from "@/components/EmployeePayrollSummary";
import { useEmployeeSession } from "@/store/EmployeeSessionContext";
import {
  formatDayHeader,
  formatHours,
  formatWeekId,
  getCurrentOrLastMonday,
  getWeekDates,
  parseWeekId,
} from "@/lib/format";
import {
  getPayroll,
  getRegistration,
  listWeeks,
  saveRegistration,
  subscribeToRegistrations,
} from "@/lib/firestore";
import { getAppConfig } from "@/lib/shiftConfig";
import type { AppConfig, PayrollDoc, RegistrationShift, ShiftBlock, WeekDoc } from "@/lib/types";

export function EmployeeShifts() {
  const { employee, logout } = useEmployeeSession();
  const navigate = useNavigate();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [weeks, setWeeks] = useState<WeekDoc[]>([]);
  const [selectedWeekId, setSelectedWeekId] = useState<string>("");
  const [selected, setSelected] = useState<Map<string, RegistrationShift>>(new Map());
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payroll, setPayroll] = useState<PayrollDoc | null>(null);

  // Bootstrap: fetch config + all weeks, then default-select the current week.
  useEffect(() => {
    if (!employee) return;
    let cancelled = false;
    (async () => {
      const [cfg, allWeeks] = await Promise.all([getAppConfig(), listWeeks()]);
      if (cancelled) return;
      setConfig(cfg);
      setWeeks(allWeeks);
      if (allWeeks.length > 0) {
        // weeks come back sorted by startDate desc. Default to the week that is
        // open for registration; if none is open, fall back to the current week,
        // then the most recent past week, then the earliest upcoming week.
        const currentId = formatWeekId(getCurrentOrLastMonday());
        const defaultWeek =
          allWeeks.find((w) => w.status === "open") ??
          allWeeks.find((w) => w.startDate <= currentId) ??
          allWeeks[allWeeks.length - 1];
        setSelectedWeekId(defaultWeek.startDate);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [employee]);

  // Load the saved registration whenever the selected week changes.
  useEffect(() => {
    if (!employee || !selectedWeekId) return;
    let cancelled = false;
    (async () => {
      const reg = await getRegistration(selectedWeekId, employee.phone);
      if (cancelled) return;
      const map = new Map<string, RegistrationShift>();
      reg?.shifts.forEach((s) => map.set(shiftKey(s.date, s.shift), s));
      setSelected(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [employee, selectedWeekId]);

  // Live "X/Y đã đăng ký" counter for the manager dashboard parity.
  useEffect(() => {
    if (!selectedWeekId) return;
    return subscribeToRegistrations(selectedWeekId, () => {
      /* no-op for now; counts will surface in manager UI */
    });
  }, [selectedWeekId]);

  // Load the official payroll for closed (read-only) weeks; clear it otherwise.
  useEffect(() => {
    if (!employee || !selectedWeekId) return;
    const wk = weeks.find((w) => w.startDate === selectedWeekId);
    const readOnly = !wk || wk.status === "approved" || wk.status === "paid";
    if (!readOnly) {
      setPayroll(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const p = await getPayroll(selectedWeekId, employee.phone);
      if (cancelled) return;
      setPayroll(p);
    })();
    return () => {
      cancelled = true;
    };
  }, [employee, selectedWeekId, weeks]);

  if (!employee) return null;

  const week = weeks.find((w) => w.startDate === selectedWeekId) ?? null;
  const monday = useMemo(
    () => (selectedWeekId ? parseWeekId(selectedWeekId) : getCurrentOrLastMonday()),
    [selectedWeekId],
  );
  const weekDates = useMemo(() => getWeekDates(monday), [monday]);
  const isReadOnly = !week || week.status === "approved" || week.status === "paid";
  const shifts: ShiftBlock[] = config?.shifts ?? [];
  const totalHours = Array.from(selected.values()).reduce((sum, s) => sum + s.hours, 0);
  const count = selected.size;

  const toggle = (date: string, shift: ShiftBlock) => {
    if (isReadOnly) return;
    setSelected((prev) => {
      const next = new Map(prev);
      const key = shiftKey(date, shift.label);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, { date, shift: shift.label, hours: shift.hours });
      }
      return next;
    });
  };

  const save = async () => {
    if (!week || isReadOnly || saving) return;
    setSaving(true);
    try {
      await saveRegistration(week.startDate, {
        phone: employee.phone,
        name: employee.name,
        updatedAt: Date.now(),
        shifts: Array.from(selected.values()),
      });
      setToast("✅ Đã lưu đăng ký");
    } catch (err) {
      console.error(err);
      setToast("❌ Lưu thất bại. Thử lại.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  if (loading || !config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-body text-gray-500">
        Đang tải…
      </div>
    );
  }

  if (!week) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-body text-gray-500">
        Chưa có tuần nào để hiển thị.
      </div>
    );
  }

  const payrollHours = payroll ? payroll.totalHours : totalHours;
  const payrollRate = payroll ? payroll.hourlyRate : config.hourlyRate;
  const payrollTotal = payroll ? payroll.totalPay : totalHours * config.hourlyRate;

  return (
    <div className="min-h-screen bg-body pb-32">
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-pink-100 bg-white/80 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Logo size={28} />
          <div className="flex flex-col gap-1">
            <div className="text-base font-bold text-gray-900">{employee.name}</div>
            <WeekSelect weeks={weeks} value={selectedWeekId} onChange={setSelectedWeekId} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              isReadOnly
                ? "bg-blue-50 text-blue-500"
                : "bg-pink-50 text-pink-600"
            }`}
          >
            {isReadOnly ? week.status : "Mở"}
          </span>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-400 hover:text-pink-500"
          >
            Thoát
          </button>
        </div>
      </header>

      {isReadOnly ? (
        <EmployeePayrollSummary
          name={employee.name}
          totalHours={payrollHours}
          hourlyRate={payrollRate}
          totalPay={payrollTotal}
          isProjected={!payroll}
          payslipUrl={payroll?.payslipUrl}
        />
      ) : (
        <>
          {weekDates.map((date, dayIdx) => {
            const isWeekend = dayIdx >= 5; // 0-4 = T2-T6, 5-6 = T7-CN
            const dayShifts = shifts.filter(
              (s) => !s.days || s.days === (isWeekend ? "weekend" : "weekday"),
            );
            return (
              <section key={date} className="mt-2">
                <h2 className="px-4 pb-2 pt-5 text-sm font-bold uppercase tracking-widest text-gray-500">
                  {formatDayHeader(monday, dayIdx)}
                </h2>
                <div className={`grid gap-3 px-4 ${dayShifts.length === 3 ? "grid-cols-3" : "grid-cols-1"}`}>
                  {dayShifts.map((shift) => {
                    const isSelected = selected.has(shiftKey(date, shift.label));
                    return (
                      <ShiftCard
                        key={shift.label}
                        shift={shift}
                        selected={isSelected}
                        disabled={isReadOnly}
                        onToggle={() => toggle(date, shift)}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })}

          <div className="fixed inset-x-0 bottom-0 border-t border-pink-100 bg-white/90 px-4 py-4 backdrop-blur-md">
            <div className="mx-auto max-w-2xl">
              <p className="mb-3 text-center text-sm font-medium text-gray-600">
                Đã chọn: {count} ca — {formatHours(totalHours)}
              </p>
              <button
                onClick={save}
                disabled={isReadOnly || saving || count === 0}
                className="btn-cta w-full"
              >
                {saving ? "Đang lưu…" : "💾 Lưu đăng ký"}
              </button>
            </div>
          </div>
        </>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function shiftKey(date: string, label: string): string {
  return `${date}__${label}`;
}
